import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { api, ApiError } from "../../shared/api/client";
import { Button, Field, InlineAlert, Input, LoadingState } from "../../shared/ui";
import { AuthShell } from "./AuthShell";

/** Mirrors the server's rule exactly, so the two cannot disagree about what is acceptable. */
const MIN_PASSWORD_LENGTH = 12;

const schema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters`)
      .max(128, "That is too long"),
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, {
    message: "The two passwords do not match",
    path: ["confirm"],
  });

type FormValues = z.infer<typeof schema>;

interface InvitationDetails {
  fullName: string;
  email: string;
  expiresAt: string;
}

/**
 * Sets the first password for an invited account (§10).
 *
 * <p>The link is validated before the form is shown. Letting someone type a password and only then
 * discover the link expired wastes their effort and, worse, leaves them unsure whether it was set.
 */
export function ActivateAccountPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () =>
      api.get<InvitationDetails>(`/api/v1/auth/invitations/${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    // An invalid link will not become valid by asking again.
    retry: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post(`/api/v1/auth/invitations/${encodeURIComponent(token)}/accept`, {
        password: values.password,
      });
      setDone(true);
    } catch (error) {
      setFormError(
        error instanceof ApiError
          ? error.message
          : "Could not set your password. Try the link again.",
      );
    }
  });

  if (!token) {
    return (
      <AuthShell title="Activation link missing">
        <InlineAlert tone="error">
          This page needs the link from your invitation email. Open that link directly.
        </InlineAlert>
      </AuthShell>
    );
  }

  if (invitation.isPending) {
    return (
      <AuthShell title="Checking your link">
        <LoadingState label="Just a moment…" />
      </AuthShell>
    );
  }

  if (invitation.isError) {
    return (
      <AuthShell title="This link is no longer valid">
        <div className="space-y-4">
          <InlineAlert tone="warning">
            Invitation links expire, and are replaced whenever a new one is sent. Ask an
            administrator to invite you again.
          </InlineAlert>
          <Button variant="secondary" className="w-full" onClick={() => navigate("/login")}>
            Back to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Your account is ready">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <p className="text-sm text-emerald-700">
              Your password has been set. Sign in with it to confirm it works.
            </p>
          </div>
          <Button className="w-full" onClick={() => navigate("/login")}>
            Go to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Welcome, ${invitation.data.fullName.split(" ")[0]}`}
      subtitle={`Choose a password for ${invitation.data.email}.`}
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <Field
          label="New password"
          required
          error={errors.password?.message}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. A memorable phrase works well.`}
        >
          <Input type="password" autoComplete="new-password" autoFocus {...register("password")} />
        </Field>

        <Field label="Confirm password" required error={errors.confirm?.message}>
          <Input type="password" autoComplete="new-password" {...register("confirm")} />
        </Field>

        <Button type="submit" loading={isSubmitting} className="w-full">
          {isSubmitting ? "Setting your password…" : "Set password"}
        </Button>

        <p className="text-center text-xs text-ink-400">
          This link works once and expires{" "}
          {new Date(invitation.data.expiresAt).toLocaleDateString()}.
        </p>
      </form>
    </AuthShell>
  );
}
